import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import IntranetLayout from "@/components/IntranetLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Camera, Lock, Globe, Bell, User, Shield, Palette, Sparkles, Loader2, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

const AVATAR_STYLES = [
  { id: "modern", label: "Modern", desc: "Clean geometric gradient" },
  { id: "cartoon", label: "Playful", desc: "Fun bubbly style" },
  { id: "watercolor", label: "Watercolor", desc: "Artistic paint wash" },
  { id: "neon", label: "Neon", desc: "Glowing cyberpunk" },
  { id: "maritime", label: "Maritime", desc: "Nautical emblem" },
  { id: "abstract", label: "Abstract", desc: "Geometric art" },
];

const AVATAR_COLORS = [
  { id: "teal and dark blue", label: "Ocean", class: "bg-teal" },
  { id: "deep navy and gold", label: "Navy", class: "bg-navy-deep" },
  { id: "coral and warm orange", label: "Coral", class: "bg-orange-500" },
  { id: "emerald green and mint", label: "Emerald", class: "bg-emerald-500" },
  { id: "purple and violet", label: "Violet", class: "bg-violet-500" },
  { id: "slate gray and silver", label: "Slate", class: "bg-slate-500" },
];

const TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "America/Sao_Paulo", "America/Argentina/Buenos_Aires",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Africa/Lagos", "Africa/Johannesburg", "Africa/Cairo",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "Pacific/Auckland",
];

const EmployeeAccountSettings = () => {
  const { user, fullName } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Profile
  const [displayName, setDisplayName] = useState(fullName || "");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Avatar generator
  const [showAvatarGen, setShowAvatarGen] = useState(false);
  const [avatarStyle, setAvatarStyle] = useState("modern");
  const [avatarColor, setAvatarColor] = useState("teal and dark blue");
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [generatedAvatarUrl, setGeneratedAvatarUrl] = useState<string | null>(null);

  // Password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Preferences
  const [timezone, setTimezone] = useState(
    () => localStorage.getItem("user_timezone") || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [compactMode, setCompactMode] = useState(
    () => localStorage.getItem("compact_mode") === "true"
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Load directory entry for avatar, job title, phone, bio
    supabase
      .from("employee_directory")
      .select("avatar_url, job_title, phone, bio, timezone")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setAvatarUrl(data.avatar_url);
          setJobTitle(data.job_title || "");
          setPhone(data.phone || "");
          setBio(data.bio || "");
          if ((data as any).timezone) setTimezone((data as any).timezone);
        }
      });
  }, [user]);

  useEffect(() => {
    setDisplayName(fullName || "");
  }, [fullName]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 2MB", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    const ext = file.name.split(".").pop();
    const path = `${user.id}/avatar.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true });

    if (uploadErr) {
      toast({ title: "Upload failed", description: uploadErr.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = urlData.publicUrl + "?t=" + Date.now();
      setAvatarUrl(url);

      // Update or insert directory entry
      const { data: existing } = await supabase
        .from("employee_directory")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        await supabase.from("employee_directory").update({ avatar_url: url }).eq("user_id", user.id);
      } else {
        await supabase.from("employee_directory").insert({ user_id: user.id, avatar_url: url, job_title: jobTitle || "Employee", department: "Operations" });
      }
      invalidateAvatarQueries();
      toast({ title: "Photo updated" });
    }
    setUploadingAvatar(false);
  };

  const invalidateAvatarQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["emp-avatar"] });
    queryClient.invalidateQueries({ queryKey: ["layout-avatar"] });
  };

  const handleGenerateAvatar = async () => {
    if (!user || !displayName) {
      toast({ title: "Please set your name first", variant: "destructive" });
      return;
    }
    setGeneratingAvatar(true);
    setGeneratedAvatarUrl(null);

    const initials = displayName
      .split(" ")
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);

    try {
      const { data, error } = await supabase.functions.invoke("generate-avatar", {
        body: { initials, style: avatarStyle, color: avatarColor },
      });

      if (error) throw error;
      if (!data?.imageUrl) throw new Error("No image returned");

      setGeneratedAvatarUrl(data.imageUrl);
    } catch (err: any) {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
    } finally {
      setGeneratingAvatar(false);
    }
  };

  const handleUseGeneratedAvatar = async () => {
    if (!user || !generatedAvatarUrl) return;
    setUploadingAvatar(true);

    try {
      // Convert base64 to blob
      const res = await fetch(generatedAvatarUrl);
      const blob = await res.blob();
      const path = `${user.id}/avatar.png`;

      const { error: uploadErr } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { upsert: true, contentType: "image/png" });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = urlData.publicUrl + "?t=" + Date.now();
      setAvatarUrl(url);

      const { data: existing } = await supabase
        .from("employee_directory")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        await supabase.from("employee_directory").update({ avatar_url: url }).eq("user_id", user.id);
      } else {
        await supabase.from("employee_directory").insert({ user_id: user.id, avatar_url: url, job_title: jobTitle || "Employee", department: "Operations" });
      }

      invalidateAvatarQueries();
      toast({ title: "Avatar saved!" });
      setShowAvatarGen(false);
      setGeneratedAvatarUrl(null);
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
    }
  };
  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);

    // Update profiles table
    await supabase.from("profiles").update({ full_name: displayName }).eq("user_id", user.id);

    // Update user metadata
    await supabase.auth.updateUser({ data: { full_name: displayName } });

    // Update directory
    const { data: existing } = await supabase.from("employee_directory").select("id").eq("user_id", user.id).maybeSingle();
    const dirData = { job_title: jobTitle || "Employee", phone, bio, timezone };

    if (existing) {
      await supabase.from("employee_directory").update(dirData).eq("user_id", user.id);
    } else {
      await supabase.from("employee_directory").insert({ user_id: user.id, ...dirData, department: "Operations" });
    }

    toast({ title: "Profile saved" });
    setSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({ title: "Password too short", description: "Minimum 8 characters", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password updated successfully" });
      setNewPassword("");
      setConfirmPassword("");
    }
    setPasswordLoading(false);
  };

  const handleSavePreferences = async () => {
    localStorage.setItem("user_timezone", timezone);
    localStorage.setItem("compact_mode", String(compactMode));
    // Also persist timezone to directory for other employees to see
    if (user) {
      const { data: existing } = await supabase.from("employee_directory").select("id").eq("user_id", user.id).maybeSingle();
      if (existing) {
        await supabase.from("employee_directory").update({ timezone }).eq("user_id", user.id);
      } else {
        await supabase.from("employee_directory").insert({ user_id: user.id, timezone, job_title: jobTitle || "Employee", department: "Operations" });
      }
    }
    toast({ title: "Preferences saved" });
  };

  const initials = (displayName || user?.email || "U").charAt(0).toUpperCase();

  return (
    <>
    <IntranetLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Account Settings</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage your profile, security, and preferences.</p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile" className="flex items-center gap-2 text-xs">
              <User className="w-3.5 h-3.5" /> Profile
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2 text-xs">
              <Shield className="w-3.5 h-3.5" /> Security
            </TabsTrigger>
            <TabsTrigger value="preferences" className="flex items-center gap-2 text-xs">
              <Palette className="w-3.5 h-3.5" /> Preferences
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Profile Photo</CardTitle>
                <CardDescription>Upload a photo or create a custom AI avatar.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 mb-4">
                  <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <Avatar className="h-20 w-20 border-2 border-border">
                      {avatarUrl ? <AvatarImage src={avatarUrl} /> : null}
                      <AvatarFallback className="text-xl font-bold bg-teal/10 text-teal">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="absolute inset-0 bg-foreground/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-6 h-6 text-background" />
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{displayName || "No name set"}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    {uploadingAvatar && <p className="text-xs text-teal mt-1">Uploading…</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Camera className="w-3.5 h-3.5 mr-1.5" /> Upload Photo
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowAvatarGen(true)} className="border-teal/30 text-teal hover:bg-teal/5">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Create Avatar
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
                <CardDescription>Update your display name and contact details.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="displayName">Full Name</Label>
                    <Input id="displayName" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input id="jobTitle" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Marine Engineer" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 123 4567" />
                </div>
                <div>
                  <Label htmlFor="bio">Short Bio</Label>
                  <textarea
                    id="bio"
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    rows={3}
                    placeholder="A few words about yourself…"
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <Button onClick={handleSaveProfile} disabled={saving} className="w-full sm:w-auto">
                  {saving ? "Saving…" : "Save Profile"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</CardTitle>
                <CardDescription>Use a strong password with at least 8 characters.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" required />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your new password" required />
                  </div>
                  <Button type="submit" disabled={passwordLoading} variant="destructive" className="w-full sm:w-auto">
                    {passwordLoading ? "Updating…" : "Update Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="text-foreground font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Created</span>
                  <span className="text-foreground font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Sign In</span>
                  <span className="text-foreground font-medium">{user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "—"}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Globe className="w-4 h-4" /> Time Zone</CardTitle>
                <CardDescription>Select your local time zone for date and time display.</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger className="w-full sm:w-80">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Bell className="w-4 h-4" /> Notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Email Notifications</p>
                    <p className="text-xs text-muted-foreground">Receive email alerts for announcements and updates</p>
                  </div>
                  <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Palette className="w-4 h-4" /> Display</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Compact Mode</p>
                    <p className="text-xs text-muted-foreground">Reduce spacing for a denser layout</p>
                  </div>
                  <Switch checked={compactMode} onCheckedChange={setCompactMode} />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSavePreferences} className="w-full sm:w-auto">Save Preferences</Button>
          </TabsContent>
        </Tabs>
      </div>
    </IntranetLayout>

    {/* Avatar Generator Dialog */}
    <Dialog open={showAvatarGen} onOpenChange={setShowAvatarGen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="w-5 h-5 text-teal" /> Create Your Avatar
          </DialogTitle>
          <DialogDescription className="font-body">
            Choose a style and color to generate a personalized avatar with your initials.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Style selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Style</Label>
            <div className="grid grid-cols-3 gap-2">
              {AVATAR_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setAvatarStyle(s.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    avatarStyle === s.id
                      ? "border-teal bg-teal/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <p className="text-sm font-medium text-foreground">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Color selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {AVATAR_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setAvatarColor(c.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    avatarColor === c.id
                      ? "border-teal bg-teal/5"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full ${c.class}`} />
                  <span className="text-xs font-medium text-foreground">{c.label}</span>
                  {avatarColor === c.id && <Check className="w-3 h-3 text-teal" />}
                </button>
              ))}
            </div>
          </div>

          {/* Preview / Generate */}
          <div className="flex flex-col items-center gap-4 py-4">
            {generatedAvatarUrl ? (
              <div className="relative">
                <Avatar className="h-32 w-32 border-4 border-border shadow-lg">
                  <AvatarImage src={generatedAvatarUrl} alt="Generated Avatar" />
                  <AvatarFallback className="text-3xl">{initials}</AvatarFallback>
                </Avatar>
              </div>
            ) : generatingAvatar ? (
              <div className="h-32 w-32 rounded-full border-4 border-border flex items-center justify-center bg-muted">
                <Loader2 className="w-8 h-8 text-teal animate-spin" />
              </div>
            ) : (
              <div className="h-32 w-32 rounded-full border-4 border-dashed border-border flex items-center justify-center bg-muted/50">
                <Sparkles className="w-8 h-8 text-muted-foreground/40" />
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleGenerateAvatar}
                disabled={generatingAvatar}
                variant="outline"
              >
                {generatingAvatar ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                ) : generatedAvatarUrl ? (
                  <><Sparkles className="w-4 h-4 mr-2" /> Regenerate</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate</>
                )}
              </Button>
              {generatedAvatarUrl && (
                <Button onClick={handleUseGeneratedAvatar} disabled={uploadingAvatar} className="bg-teal hover:bg-teal/90 text-background">
                  {uploadingAvatar ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Use This Avatar</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EmployeeAccountSettings;
